import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Circle,
  Flex,
  HStack,
  Radio,
  RadioGroup,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import PieByStage from "./PieByStage";
import PieByGroup from "./PieByGroup";
import {
  filterQuestionsByOwnerOrAssignee,
  getGroupDashboardData,
  getStageDashboardData,
  stageColors,
} from "../../dashboardDataUtils";
import { useAuth } from "../../../../../context";
import { useGetStagesOfRound } from "../../../../../hooks/query";
import { useSelector } from "react-redux";
import { H2Dashboard } from "components/text/headings";
import GapAnalysis from "../GapAnalysis/GapAnalysis";
import { User, Users } from "@phosphor-icons/react";

const TABS = [
  {
    name: "Stage",
    enabled: true,
  },
  //   { TODO
  //     name: "Complete",
  //     enabled: false,
  //   },
  //   {
  //     name: "Confidence",
  //     enabled: false,
  //   },
];

const Status = ({ questions }) => {
  const [selectedRequirement, setSelectedRequirement] = useState("all");
  const [dashboardQuestions, setDashboardQuestions] = useState(questions);
  const { user } = useAuth();

  const roundSelector = useSelector((state) => state.issbRound);

  const questionnaireId = roundSelector.selectedRound?.id;

  useEffect(() => {
    if (selectedRequirement === "all") {
      setDashboardQuestions(questions);
    } else {
      setDashboardQuestions(filterQuestionsByOwnerOrAssignee(questions, user.email));
    }
  }, [selectedRequirement, questions, user.email]);

  return (
    <Flex direction="column" gap={4} width="100%" mt={4}>
      <H2Dashboard>Round Progress</H2Dashboard>

      <Flex direction="column" gap={4}>       
        <Flex width="fit-content" alignSelf="flex-end">
          <ToggleButton options={[
			{label:"All", value:"all", icon: <Users size={18} />},
			{label:"My", value:"my", icon: <User size={18} />}]} 
		  	selected={selectedRequirement} setSelected={setSelectedRequirement} />
        </Flex>

        <StageCharts questionnaireId={questionnaireId} questions={dashboardQuestions} />

        <GapAnalysis questions={dashboardQuestions} />
      </Flex>
    </Flex>
  );
};

/**
 * 
 * @typedef {Object} Option
 * @property {string?} label
 * @property {string} value
 * @property {React.ReactNode?} icon
 * 
 * @param {Object} props
 * @param {Option[]} props.options: [{label, value, icon}]
 * @param {string} props.selected
 * @param {Function} props.setSelected
 * @param {string} props.colorOn Note: gray.400 provides a fair level of contrast See ESG-3013
 * @param {string} props.colorOff
 * @param {string} props.colorHover
 * @returns 
 */
const ToggleButton = ({options, selected, setSelected, colorOn = "gray.400", colorOff = "white", colorHover = "gray.100"}) => {
	return (<ButtonGroup isAttached width="100%">
		{options.map(option => <Tooltip label="Show data for all requirements">
		<Button
			minW="100px"
			onClick={() => setSelected(option.value)}
			variant={selected === option.value ? "solid" : "outline"}
			bg={selected === option.value ? colorOn : colorOff}
			_hover={{bg: selected === option.value ? colorOn : colorHover}}
			flex="1"
			rightIcon={option.icon}
		>{option.label || option.value}</Button>
		</Tooltip>
	)}
  </ButtonGroup>);
};

const StageCharts = ({ questionnaireId, questions }) => {
  const { data: stages } = useGetStagesOfRound(questionnaireId, {
    cacheTime: 0,
  });

  const stageDashboardData = getStageDashboardData(questions, null);
  // filter to stages with data to simplify the legend below
  const stagesWithData = stages?.filter((stage) => stageDashboardData.find((sdd) => sdd.id === stage.name));

  return (
    <Flex direction="column" bg="gray.50" borderRadius="lg" border="solid 1px" borderColor="gray.200" p={4}>
      <Text color="gray.700" fontSize="heading.xs" fontWeight="bold">
        Stage
      </Text>
      {/* legend */}
      <Table variant="simple" width="auto" size="sm" my={4}>
        <Tbody fontSize="body.s">
          <Flex as="tr" wrap="wrap">
            {stagesWithData?.map((stage) => (
              <Td key={stage.id}>
                <HStack>
                  <Circle size="10px" bg={stageColors[stage.stageType]} />
                  <Text color="teal">{stage.name}</Text>
                </HStack>
              </Td>
            ))}
          </Flex>
        </Tbody>
      </Table>

      <Flex direction="row" gap={2} wrap="wrap">
        <Box flex="1" minWidth="200px" maxWidth="500px" width="100%" height="auto">
          <PieByStage stageData={stageDashboardData} />
        </Box>
        <Box flex="1" minWidth="200px" maxWidth="500px" width="100%" height="auto">
          <PieByGroup groupData={getGroupDashboardData(questions, null)} />
        </Box>
      </Flex>
    </Flex>
  );
};

export default Status;
